import { resolveMeta, type MetaInput } from '@/lib/seo/metadata';

/**
 * Renders the meta tags setMeta() (docs/index.html:4563) wrote imperatively.
 *
 * React 19 hoists <title>, <meta> and <link> into <head> from anywhere in the
 * tree — including during renderToString, which is what makes the prerendered
 * HTML carry them without react-helmet-async.
 *
 * The ld+json <script> is rendered in place rather than hoisted (React 19 only
 * hoists scripts with src+async). That is fine: JSON-LD is valid anywhere in
 * the document and crawlers read it from the body. It keeps the prototype's
 * id="ldjson" so the markup is recognisable.
 */
export function Seo(props: MetaInput) {
  const m = resolveMeta(props);

  return (
    <>
      <title>{m.title}</title>
      <meta name="description" content={m.description} />
      <link rel="canonical" href={m.canonical} />

      <meta property="og:title" content={m.title} />
      <meta property="og:description" content={m.description} />
      <meta property="og:url" content={m.canonical} />
      <meta property="og:type" content={m.ogType} />
      <meta property="og:site_name" content={m.siteName} />
      {m.ogImage ? <meta property="og:image" content={m.ogImage} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={m.title} />
      <meta name="twitter:description" content={m.description} />

      <script
        id="ldjson"
        type="application/ld+json"
        // The payload is our own JSON.stringify of server data, never raw user
        // HTML. </script> inside a string would break out of the tag, so the
        // sequence is escaped — the standard JSON-LD precaution.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(m.jsonld).replace(/</g, '\\u003c') }}
      />
    </>
  );
}
